/**
 * Utility functions for handling image URLs
 */
import { API_ORIGIN } from '../services/api';

/**
 * Convert a file path to a full image URL
 * @param imagePath - The image path (can be relative or absolute)
 * @returns Full URL for the image
 */
export const getImageUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) {
    return null;
  }

  // Keep frontend public asset paths as-is (e.g. /assets/...)
  if (imagePath.startsWith('/assets/') || imagePath.startsWith('/images/')) {
    return imagePath;
  }
  
  // Filter out placeholder service URLs that may not resolve
  if (imagePath.includes('via.placeholder.com') || 
      imagePath.includes('placeholder.com') ||
      imagePath.includes('dummyimage.com') ||
      imagePath.startsWith('FFFFFF')) {
    return null;
  }
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  if (imagePath.startsWith('blob:') || imagePath.startsWith('data:')) {
    return imagePath;
  }

  // API serves uploads via /api/uploads/stream/:filename (not static /uploads)
  const segments = imagePath.split(/[/\\]/).filter(Boolean);
  const fileName = segments.pop();
  if (!fileName) {
    return null;
  }

  const origin = API_ORIGIN || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!origin) {
    return null;
  }

  return `${origin}/api/uploads/stream/${encodeURIComponent(fileName)}`;
};

/**
 * Get image URL with fallback
 * @param imagePath - The image path
 * @param fallback - Fallback image path or URL
 * @returns Image URL or fallback
 */
export const getImageUrlWithFallback = (
  imagePath: string | null | undefined,
  fallback?: string
): string => {
  const url = getImageUrl(imagePath);
  return url || fallback || '/images/placeholder.jpg';
};
